<script setup lang="ts">
import { useAddressesStore } from '@/store/addresses/addresses'

import {
  DeliveryMethods,
  SetDeliveryMethodPayload,
} from '@/api/modules/basket/types'

import AddressSystemListShadow from '@/components/AddressSystem/AddressSystemListShadow.vue'
import AddressSystemUserAddress from '@/components/AddressSystem/user/AddressSystemUserAddress.vue'

import type { AddressClient } from '@/api/modules/addresses/addresses.types'

type Props = { deliveryBasket: SetDeliveryMethodPayload }

type Emits = {
  (fn: 'onChangeSelectedAddress', payload: SetDeliveryMethodPayload): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { $metrics } = useNuxtApp()

const addressesStore = useAddressesStore()

const addresses = computed(() => addressesStore.addresses.data)

const onSelectAddress = (address: AddressClient) => {
  const { is_in_delivery_area, allow_redirect, city_domain_prefix, id } =
    address

  if (!is_in_delivery_area) return

  emit('onChangeSelectedAddress', {
    delivery_method: DeliveryMethods.COURIER,
    city_domain_prefix,
    allow_redirect,
    address_id: id,
  })

  $metrics?.eventSendGoal({ ym: 'saved_address_used' })
}

onBeforeUnmount(() => {
  $metrics?.eventSendGoal({ ym: 'basket_saved_address_window__closed' })
})

const radioGroupValue = computed(() => {
  return props.deliveryBasket?.address_id ?? ''
})
</script>

<template>
  <div class="address-system-user">
    <div class="address-system-user__list">
      <address-system-list-shadow v-if="addresses.length" effect-shadow>
        <the-radio-group
          name="addressSystemUser"
          :model-value="radioGroupValue"
        >
          <address-system-user-address
            v-for="address in addresses"
            :key="address.id"
            :address-id="address.id"
            :address="address"
            @keyup.enter="onSelectAddress(address)"
            @click="onSelectAddress(address)"
          />
        </the-radio-group>
      </address-system-list-shadow>
    </div>
  </div>
</template>

<style scoped lang="scss">
.address-system-user {
  &__list {
    position: relative;

    :deep(.container-shadow--active) {
      display: flex;
      flex-direction: column;
      gap: 16px;
      width: initial;
      height: 216px !important;
      padding-bottom: 2px;
    }
  }

  .app-radio-group {
    gap: 16px;
  }
}
</style>
